/**
 * KlaviyoForm - Custom HTML Form Element for Klaviyo integrations
 *
 * Usage:
 * <form is="klaviyo-form"
 *   data-api-key="your-api-key"
 *   data-list-id="your-list-id">
 *
 *   <input type="email" name="email" required>
 *   <input type="text" name="firstName">
 * </form>
 */
export class KlaviyoForm extends HTMLElement {
  private form: HTMLFormElement
  private apiKey: string
  private listId: string
  private apiRevision: string
  private endpoint: string
  private validators: Map<string, (value: string) => boolean>
  private wrapper: HTMLElement | null
  private successElement: HTMLElement | null
  private baseElement: HTMLElement | null

  constructor() {
    super()
    // Create internal form element
    this.form = document.createElement('form')
    // Move all children to the form
    while (this.firstChild) {
      this.form.appendChild(this.firstChild)
    }
    // Copy attributes from custom element to form
    Array.from(this.attributes).forEach(attr => {
      if (attr.name !== 'is') {
        this.form.setAttribute(attr.name, attr.value)
      }
    })
    this.appendChild(this.form)

    // Initialize from data attributes
    this.apiKey = this.dataset.apiKey || ''
    this.listId = this.dataset.listId || ''
    this.apiRevision = '2025-01-15'
    this.endpoint = this.dataset.endpoint || 'https://a.klaviyo.com/client/subscriptions'
    this.wrapper = this.closest('.klaviyo-form') || null
    this.successElement = this.wrapper?.querySelector('[data-klaviyo-success]') || null
    this.baseElement = this.wrapper?.querySelector('[data-klaviyo-input]') || null
    // Set up validators
    this.validators = new Map()
    this._setupDefaultValidators()
    this._setupCustomValidators()

    // Add submit handler
    this.form.addEventListener('submit', this._submitForm.bind(this))
  }

  /**
     * Set up default validators for common field types
     * @private
     */
  private _setupDefaultValidators() {
    // Email validator that checks for unicode characters incompatible with ERP
    this.validators.set('email', (value: string) => {
      // Basic email format validation
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!emailRegex.test(value)) {
        return false
      }

      // Check for incompatible unicode characters (ASCII only for ERP compatibility)
      // eslint-disable-next-line no-control-regex
      const asciiOnly = /^[\u0000-\u007F]*$/.test(value)
      return asciiOnly
    })

    // Phone number validator
    this.validators.set('phone', (value: string) => {
      // Simple phone format validation, adjust as needed
      return /^\+?[0-9]{10,15}$/.test(value)
    })
  }

  /**
     * Set up custom validators from data attributes
     * @private
     */
  private _setupCustomValidators() {
    // Extract custom validators from data attributes
    // Format: data-validate-fieldname="validatorName"
    Array.from(this.attributes)
      .filter(attr => attr.name.startsWith('data-validate-'))
      .forEach(attr => {
        const fieldName = attr.name.replace('data-validate-', '')
        const validatorName = attr.value

        // Use custom validator if defined, otherwise keep default
        if (validatorName && this.validators.has(validatorName)) {
          this.validators.set(fieldName, this.validators.get(validatorName)!)
        }
      })
  }

  /**
     * Validate all form fields
     * @param formData Form data to validate
     * @private
     */
  private _validateForm(formData: FormData): { valid: boolean, errors: Record<string, string> } {
    const errors: Record<string, string> = {}
    let valid = true

    // Apply validators to form fields
    for (const [name, value] of formData.entries()) {
      if (typeof value === 'string' && this.validators.has(name)) {
        const isValid = this.validators.get(name)!(value)
        if (!isValid) {
          errors[name] = `Invalid ${name} format`
          valid = false
        }
      }
    }

    return { valid, errors }
  }

  /**
     * Handle form submission
     * @param e Submit event
     * @private
     */
  private async _submitForm(e: Event) {
    e.preventDefault()
    const formData = new FormData(this.form)

    // Validate the form
    const validation = this._validateForm(formData)
    if (!validation.valid) {
      this._showValidationErrors(validation.errors)
      return
    }

    // Process form data
    const data = Object.fromEntries(formData.entries())
    console.log(data)

    // Prepare the payload for Klaviyo
    const payload = this._preparePayload(data)

    try {
      // Send data to Klaviyo
      const response = await this._sendToKlaviyo(payload)
      this._handleResponse(response)
    } catch (error) {
      this._handleError(error)
    }
  }

  /**
     * Display validation errors in the form
     * @param errors Validation errors
     * @private
     */
  private _showValidationErrors(errors: Record<string, string>) {
    // Clear previous error messages
    const errorElements = this.form.querySelectorAll('.klaviyo-error')
    errorElements.forEach(el => el.remove())

    // Add new error messages
    for (const [field, message] of Object.entries(errors)) {
      const inputElement = this.form.querySelector(`[name="${field}"]`)
      if (inputElement) {
        const errorElement = document.createElement('div')
        errorElement.className = 'klaviyo-error'
        errorElement.textContent = message
        errorElement.style.color = 'red'
        errorElement.style.fontSize = '0.8rem'

        inputElement.parentNode?.insertBefore(errorElement, inputElement.nextSibling)
      }
    }

    // Dispatch custom event
    this.dispatchEvent(new CustomEvent('klaviyo:validation-failed', {
      detail: { errors }
    }))
  }

  /**
 * Prepare payload for Klaviyo API
 * @param formData Form data
 * @private
 */
  private _preparePayload(formData: Record<string, any>) {
    // Build profile attributes object with only populated fields
    const profileAttributes: Record<string, any> = {}

    // Add basic profile fields only if they exist
    if (formData.email) profileAttributes.email = formData.email
    if (formData.firstName) profileAttributes.first_name = formData.firstName
    if (formData.lastName) profileAttributes.last_name = formData.lastName
    if (formData.phoneNumber) profileAttributes.phone_number = formData.phoneNumber

    // Add location data only if it exists
    const locationData = this._extractLocationData(formData)
    if (locationData && Object.keys(locationData).length > 0) {
      profileAttributes.location = locationData
    }

    // Add custom properties only if they exist
    const customProperties = this._extractCustomProperties(formData)
    if (customProperties && Object.keys(customProperties).length > 0) {
      profileAttributes.properties = customProperties
    }

    // Add subscription data only if consent fields are present
    const subscriptions: Record<string, any> = {}
    const consentedAt = new Date().toISOString()

    if (Object.prototype.hasOwnProperty.call(formData, 'emailConsent')) {
      subscriptions.email = {
        marketing: {
          consent: formData.emailConsent === 'on' ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
          consented_at: consentedAt
        }
      }
    }

    if (Object.prototype.hasOwnProperty.call(formData, 'smsConsent')) {
      subscriptions.sms = {
        marketing: {
          consent: formData.smsConsent === 'on' ? 'SUBSCRIBED' : 'UNSUBSCRIBED',
          consented_at: consentedAt
        }
      }
    }

    // Only add subscriptions if any exist
    if (Object.keys(subscriptions).length > 0) {
      profileAttributes.subscriptions = subscriptions
    }

    // Build the payload with only populated attributes
    const payload: Record<string, any> = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.listId
            }
          }
        }
      }
    }

    // Add custom source only if it exists
    const customSource = formData.source || this.dataset.source
    if (customSource) {
      payload.data.attributes.custom_source = customSource
    } else if (this.dataset.source) {
      payload.data.attributes.custom_source = this.dataset.source
    }

    return payload
  }

  /**
     * Extract location data from form fields
     * @param formData Form data
     * @private
     */
  private _extractLocationData(formData: Record<string, any>): Record<string, any> | null {
    const locationFields = ['address1', 'address2', 'city', 'country', 'region', 'zip', 'timezone']
    const location: Record<string, any> = {}

    for (const field of locationFields) {
      if (formData[field]) {
        location[field] = formData[field]
      }
    }

    // Add IP address if available
    if (formData.ip) {
      location.ip = formData.ip
    }

    return Object.keys(location).length > 0 ? location : null
  }

  /**
     * Extract custom properties from form data
     * @param formData Form data
     * @private
     */
  private _extractCustomProperties(formData: Record<string, any>): Record<string, any> {
    // Standard fields that should not be included in properties
    const standardFields = [
      'email', 'firstName', 'lastName', 'phoneNumber', 'source',
      'emailConsent', 'smsConsent', 'address1', 'address2',
      'city', 'country', 'region', 'zip', 'timezone', 'ip'
    ]

    const customProps: Record<string, any> = {}

    for (const [key, value] of Object.entries(formData)) {
      if (!standardFields.includes(key)) {
        customProps[key] = value
      }
    }

    return customProps
  }

  /**
     * Send data to Klaviyo API
     * @param payload API payload
     * @private
     */
  private async _sendToKlaviyo(payload: any) {
    const options = {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        revision: this.apiRevision,
        'content-type': 'application/vnd.api+json'
      },
      body: JSON.stringify(payload)
    }

    const response = await fetch(`${this.endpoint}?company_id=${this.apiKey}`, options)

    if (!response.ok) {
      const errorData = await response.json().catch(() => null)
      throw new Error(`Klaviyo API error: ${response.status} ${response.statusText} - ${errorData?.detail}`)
    }

    return { status: response.status, message: 'Subscription successful' }
  }

  /**
     * Handle successful API response
     * @param response API response
     * @private
     */
  private _handleResponse(response: any) {
    console.log('Klaviyo subscription successful:', response)

    // Clear the form
    this.form.reset()

    // Dispatch success event
    this.dispatchEvent(new CustomEvent('klaviyo:success', {
      detail: { response }
    }))

    // Show success message if there's an element for it
    if (this.successElement) {
      this.successElement.classList.remove('hidden')
      this.baseElement?.classList.add('hidden')
    }
  }

  /**
     * Handle API error
     * @param error Error object
     * @private
     */
  private _handleError(error: any) {
    console.error('Klaviyo subscription error:', error)

    // Dispatch error event
    this.dispatchEvent(new CustomEvent('klaviyo:error', {
      detail: { error }
    }))

    // Show error message if there's an element for it
    const errorElement = this.form.querySelector('[data-klaviyo-error]')
    if (errorElement) {
      errorElement.classList.remove('hidden')
      errorElement.textContent = error.message || 'Failed to subscribe. Please try again later.'
    }
  }

  /**
     * Add a custom validator for a field
     * @param fieldName Field name
     * @param validator Validator function
     * @public
     */
  addValidator(fieldName: string, validator: (value: string) => boolean) {
    this.validators.set(fieldName, validator)
    return this
  }

  /**
     * Validate a specific field
     * @param fieldName Field name
     * @param value Field value
     * @returns Whether the field is valid
     * @public
     */
  validateField(fieldName: string, value: string): boolean {
    if (this.validators.has(fieldName)) {
      return this.validators.get(fieldName)!(value)
    }
    return true // If no validator exists, consider it valid
  }
}
